import { Box, Stack, useTheme } from "@mui/material";
import { useCallback, useEffect } from "react";

import { DollarBillSvg } from "../HiringInstantPayDollarBill";
import { InstantPaySlider } from "./InstantPaySlider";
import { InstantPaySummary } from "./InstantPaySummary";
import { useDollarBillAnimation } from "./useDollarBillAnimation";

interface InstantPayVisualizationProps {
  totalPay: number;
  defaultValue: number;
  onChange: (amount: number) => void;
}

const CANVAS_WIDTH = 120;
const CANVAS_HEIGHT = 130;
const BILL_WIDTH = 50;
const BILL_HEIGHT = 25;
const BILL_VALUE = 1;
const MAX_BILLS = 15;

export function InstantPayVisualization(props: InstantPayVisualizationProps) {
  const { totalPay, defaultValue, onChange } = props;
  const theme = useTheme();

  const eligibleAmount = totalPay * 0.6;

  // Reset slider to show at least 1 bill whenever total pay changes
  useEffect(() => {
    if (eligibleAmount > 0) {
      // Calculate minimum amount to show 1 bill
      const minAmount = Math.ceil(eligibleAmount / MAX_BILLS);
      onChange(minAmount);
    } else {
      onChange(0);
    }
  }, [totalPay, onChange, eligibleAmount]);

  const normalizedBillCount =
    eligibleAmount > 0 ? Math.round((defaultValue / eligibleAmount) * MAX_BILLS) : 0;

  const { bills } = useDollarBillAnimation({
    targetBillCount: normalizedBillCount,
    billWidth: BILL_WIDTH,
    jarX: (CANVAS_WIDTH - 80) / 2,
    jarY: CANVAS_HEIGHT - 120,
    jarWidth: 80,
    jarHeight: 120,
  });

  const handleSliderChange = useCallback(
    (_event: Event, value: number | number[]) => {
      const newValue = Array.isArray(value) ? value[0] : value;
      onChange(newValue);
    },
    [onChange]
  );

  return (
    <Stack spacing={3}>
      {/* Pay Summary Text and Money Stack */}
      <Box
        sx={{
          display: "flex",
          gap: 3,
          alignItems: "flex-end",
          position: "relative",
          overflow: "visible",
        }}
      >
        {/* Left side: Pay Summary */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "flex-end",
            paddingBottom: 2,
          }}
        >
          <InstantPaySummary totalPay={totalPay} />
        </Box>

        {/* Right side: Money stack */}
        {totalPay > 0 && (
          <Box
            sx={{
              width: CANVAS_WIDTH,
              height: CANVAS_HEIGHT,
              flexShrink: 0,
              position: "relative",
              zIndex: 0,
            }}
          >
            <svg
              width="100%"
              height="100%"
              viewBox={`0 0 ${CANVAS_WIDTH} ${CANVAS_HEIGHT}`}
              style={{ display: "block", overflow: "visible" }}
            >
              {bills.map((bill) => (
                <DollarBillSvg
                  key={bill.id}
                  x={bill.x}
                  y={bill.y}
                  rotation={bill.rotation}
                  width={BILL_WIDTH}
                  height={BILL_HEIGHT}
                  fillColor={theme.palette.success.light}
                  strokeColor={theme.palette.success.dark}
                />
              ))}
            </svg>
          </Box>
        )}
      </Box>

      {/* Slider - Full Width */}
      {totalPay > 0 && (
        <InstantPaySlider
          payoutAmount={defaultValue}
          totalPay={eligibleAmount}
          billValue={BILL_VALUE}
          onSliderChange={handleSliderChange}
        />
      )}
    </Stack>
  );
}
